import {
  addLayer,
  clearActiveLayer,
  deleteLayer,
  floodFillActiveLayer,
  getCurrentBrushSize,
  getCurrentColor,
  paintEllipse,
  paintLine,
  paintRectangle,
  selectFrame,
  selectLayer,
  selectTool,
  setFrameCount,
  setLayerVisibility,
  setOnionEnabled,
  validateCurrentStateMachine,
} from "#puf/lib/assistant-actions.ts";
import { getOrCreateDesignSessionName, summarizeEditorContext } from "#puf/lib/design-agent.ts";
import {
  DEFAULT_COLS,
  DEFAULT_ROWS,
  indicesToGrid,
  replaceLayerData,
  resolvePalette,
  tilemapToGrid,
} from "#puf/lib/palette-paint.ts";
import { store } from "#puf/store/index.ts";
import { getToolOutput, getToolPartState, useAgentChat } from "@cloudflare/ai-chat/react";
import {
  CommitFrameInput,
  CommitTilemapInput,
  ProposeStateMachineOutput,
  ScaffoldAnimationSetInput,
} from "@pixelpuf/schemas/ai";
import { HexColorSchema, ProjectNameSchema } from "@pixelpuf/schemas/primitives";
import { expandRle } from "@pixelpuf/schemas/pxpf";
import { ToolModeSchema, ToolSchema } from "@pixelpuf/schemas/tool";
import { useSelector } from "@xstate/store-react";
import { useAgent } from "agents/react";
import { isTextUIPart, isToolUIPart, type UIMessage } from "ai";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";

const MAX_TOOL_INPUT_ATTEMPTS = 2;

const ToolChoiceSchema = z.enum([...ToolSchema.options, "PICKER"]);
const SetColorInput = z.object({ color: HexColorSchema });
const SetFpsInput = z.object({ fps: z.coerce.number().int().min(1).max(60) });
const SetBrushSizeInput = z.object({ size: z.coerce.number().int().min(1).max(6) });
const SetModeInput = z.object({ mode: ToolModeSchema });
const SetOnionEnabledInput = z.object({ enabled: z.boolean() });
const SetOnionOpacityInput = z.object({ value: z.coerce.number().min(0).max(1) });
const SetProjectNameInput = z.object({ name: ProjectNameSchema });
const SetTabInput = z.object({ tab: z.enum(["canvas", "state"]) });
const SetFrameCountInput = z.object({ totalFrames: z.coerce.number().int().min(1).max(64) });
const SetActiveFrameInput = z.object({ frameNumber: z.coerce.number().int().min(1).max(64) });
const AddLayerInput = z.object({ count: z.coerce.number().int().min(1).max(4).optional() });
const SelectLayerInput = z.object({ layerId: z.string().min(1) });
const SetLayerVisibilityInput = z.object({ layerId: z.string().min(1), visible: z.boolean() });
const DeleteLayerInput = z.object({ layerId: z.string().min(1) });
const SelectToolInput = z.object({ tool: ToolChoiceSchema });
const PointInput = z.object({
  row: z.coerce.number().int().min(0).max(31),
  col: z.coerce.number().int().min(0).max(31),
});
const ClearActiveLayerInput = z.object({ reason: z.string().optional() });
const PaintLineInput = z.object({
  from: PointInput,
  to: PointInput,
  color: HexColorSchema.optional(),
  brushSize: z.coerce.number().int().min(1).max(6).optional(),
});
const PaintShapeInput = z.object({
  from: PointInput,
  to: PointInput,
  color: HexColorSchema.optional(),
  filled: z.boolean().optional(),
  brushSize: z.coerce.number().int().min(1).max(6).optional(),
});
const FloodFillInput = z.object({
  cell: PointInput,
  color: HexColorSchema.optional(),
});
const GeneratePaletteInput = z.object({
  theme: z.string().min(1).max(200).optional(),
  name: z.string().min(1).max(200).optional(),
  size: z.coerce.number().int().min(2).max(64).optional(),
});

const QUICK_PROMPTS = [
  "Start a 6-frame frog hop with a swamp palette.",
  "Give this project a cozy sunset palette and set a good base color.",
  "Set up idle, hop, and land states for a simple loop.",
] as const;

type AssistantMessage = UIMessage;
type AssistantPart = UIMessage["parts"][number];

interface ToolCallLike {
  input: unknown;
  toolCallId: string;
  toolName: string;
}

interface ToolCallEvent {
  addToolOutput: (output: { toolCallId: string; output: unknown }) => void;
  toolCall: ToolCallLike;
}

interface ParsedFallbackToolCall {
  key: string;
  messageId: string;
  toolCall: ToolCallLike;
}

interface FallbackToolResult {
  key: string;
  output: unknown;
  toolName: string;
}

interface ToolExecutionContext {
  invalidInputAttempts?: Map<string, number>;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function AIAssistant({ open, onClose }: Props) {
  const [draft, setDraft] = useState("");
  const [sessionName] = useState(() => getOrCreateDesignSessionName());
  const [fallbackResults, setFallbackResults] = useState<Record<string, FallbackToolResult[]>>({});
  const endRef = useRef<HTMLDivElement | null>(null);
  const handledFallbackToolCalls = useRef<Set<string>>(new Set());
  const handledProposalResults = useRef<Set<string>>(new Set());
  const invalidInputAttempts = useRef<Map<string, number>>(new Map());
  const pendingProposal = useSelector(store, (snapshot) => snapshot.context.pendingProposal);
  const agent = useAgent({ agent: "DesignAgent", name: sessionName });
  const { clearHistory, messages, sendMessage, status } = useAgentChat({
    agent,
    body: () => ({ editor: summarizeEditorContext(store.getSnapshot().context) }),
    onToolCall: async ({ addToolOutput, toolCall }: ToolCallEvent) => {
      const output = await executeToolCall(toolCall, { invalidInputAttempts: invalidInputAttempts.current });
      if (output === null) return;
      addToolOutput({ toolCallId: toolCall.toolCallId, output });
    },
  });

  const submitPrompt = (prompt: string) => {
    invalidInputAttempts.current.clear();
    void sendMessage({ text: prompt });
  };

  const submitDraft = () => {
    const next = draft.trim();
    if (next === "" || status !== "ready") return;
    submitPrompt(next);
    setDraft("");
  };

  useEffect(() => {
    for (const message of messages) {
      for (const parsed of extractFallbackToolCalls(message)) {
        if (handledFallbackToolCalls.current.has(parsed.key)) continue;
        handledFallbackToolCalls.current.add(parsed.key);
        void executeToolCall(parsed.toolCall).then((output) => {
          if (output === null) return;
          setFallbackResults((current) => {
            const existing = current[parsed.messageId] ?? [];
            return {
              ...current,
              [parsed.messageId]: [...existing, { key: parsed.key, output, toolName: parsed.toolCall.toolName }],
            };
          });
        });
      }
    }
  }, [messages]);

  // Pick up server-executed `propose_state_machine` tool outputs as they
  // stream in and surface them to the MachineEditor proposal panel via
  // `proposal.set`. Handled-set is keyed by message + part index so we
  // don't re-dispatch on every render.
  useEffect(() => {
    for (const message of messages) {
      if (message.role !== "assistant") continue;
      for (const [index, part] of message.parts.entries()) {
        if (!isToolUIPart(part)) continue;
        const toolName = part.type.startsWith("tool-") ? part.type.slice(5) : part.type;
        if (toolName !== "propose_state_machine") continue;
        if (getToolPartState(part) !== "complete") continue;
        const key = `${message.id}:${String(index)}`;
        if (handledProposalResults.current.has(key)) continue;
        const output = getToolOutput(part);
        const parsed = ProposeStateMachineOutput.safeParse(output);
        if (!parsed.success) continue;
        handledProposalResults.current.add(key);
        store.trigger["proposal.set"]({
          proposal: {
            kind: "stateMachine",
            proposal: parsed.data.proposal,
            rationale: parsed.data.rationale,
            proposedAt: new Date().toISOString(),
          },
        });
        store.trigger["tab.set"]({ tab: "state" });
      }
    }
  }, [messages]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 lg:bg-black/10">
      <button
        aria-label="Close assistant backdrop"
        className="absolute inset-0"
        onClick={onClose}
        type="button"
      />
      <aside className="absolute inset-x-0 bottom-0 top-14 flex flex-col border-t border-line bg-bg-2 shadow-2xl lg:top-17 lg:right-4 lg:left-auto lg:bottom-4 lg:w-md lg:rounded-sm lg:border">
        <header className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
          <div className="space-y-1">
            <p className="text-[10px] font-semibold uppercase tracking-[1.4px] text-dim">Studio Copilot</p>
            <p className="text-sm text-text">Scaffold frames, layers, loops, palettes, and rough pixel blockouts.</p>
            <p className="text-xs text-dim">
              It can set projects up fast and sketch basics. Still not a full autonomous artist.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="rounded-sm border border-line bg-bg-3 px-2 py-1 text-xs text-text hover:border-accent"
              onClick={() => {
                handledFallbackToolCalls.current.clear();
                invalidInputAttempts.current.clear();
                setFallbackResults({});
                clearHistory();
              }}
              type="button"
            >
              New chat
            </button>
            <button
              aria-label="Close assistant"
              className="rounded-sm border border-line bg-bg-3 px-2 py-1 text-xs text-text hover:border-accent"
              onClick={onClose}
              type="button"
            >
              Close
            </button>
          </div>
        </header>

        <div className="border-b border-line px-4 py-3">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-[1.2px] text-dim">Try one</div>
          <div className="flex flex-wrap gap-2">
            {QUICK_PROMPTS.map((prompt) => (
              <button
                key={prompt}
                className="rounded-sm border border-line bg-bg-3 px-2.5 py-1.5 text-left text-xs text-text hover:border-accent disabled:opacity-50"
                disabled={status !== "ready"}
                onClick={() => {
                  submitPrompt(prompt);
                }}
                type="button"
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>

        {pendingProposal && (
          <div className="border-b border-accent/40 bg-accent/8 px-4 py-3">
            <div className="mb-2 flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[1.2px] text-dim">Pending proposal</div>
                <div className="text-sm font-semibold text-text">{pendingProposal.proposal.character}</div>
                <div className="text-xs text-dim">
                  {String(pendingProposal.proposal.states.length)}{" "}
                  state{pendingProposal.proposal.states.length === 1 ? "" : "s"}
                  {" · "}
                  {String(pendingProposal.proposal.states.reduce((sum, state) => sum + state.frameCount, 0))} frame
                  {pendingProposal.proposal.states.reduce((sum, state) => sum + state.frameCount, 0) === 1 ? "" : "s"}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  className="rounded-sm border border-line bg-bg-3 px-2 py-1 text-xs text-text hover:border-accent"
                  onClick={() => {
                    store.trigger["proposal.apply"]();
                    store.trigger["tab.set"]({ tab: "canvas" });
                  }}
                  type="button"
                >
                  Apply
                </button>
                <button
                  className="rounded-sm border border-line bg-bg-3 px-2 py-1 text-xs text-text hover:border-accent"
                  onClick={() => {
                    store.trigger["proposal.clear"]();
                  }}
                  type="button"
                >
                  Dismiss
                </button>
              </div>
            </div>
            {pendingProposal.rationale.trim().length > 0 && (
              <p className="text-xs leading-5 text-dim">{pendingProposal.rationale}</p>
            )}
          </div>
        )}

        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {messages.length === 0
            ? (
              <div className="rounded-sm border border-dashed border-line bg-bg-3 p-3 text-sm text-dim">
                Ask for a blank animation scaffold, palette, state loop, or a rough sprite blockout.
              </div>
            )
            : messages.map((message) => renderMessage(message, fallbackResults[message.id] ?? []))}
          <div ref={endRef} />
        </div>

        <form
          className="border-t border-line p-3"
          onSubmit={(event) => {
            event.preventDefault();
            submitDraft();
          }}
        >
          <div className="mb-2 text-xs text-dim">
            Status: <span className="font-semibold text-text">{status}</span>
          </div>
          <div className="flex gap-2">
            <textarea
              className="min-h-24 flex-1 resize-none rounded-sm border border-line bg-bg-3 px-3 py-2 text-sm text-text outline-none focus:border-accent"
              onChange={(event) => {
                setDraft(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter" || !event.ctrlKey || event.nativeEvent.isComposing) return;
                event.preventDefault();
                submitDraft();
              }}
              placeholder="Try: Start a 6-frame frog hop, give it a swamp palette, and switch me to Draw."
              value={draft}
            />
            <button
              className="self-end rounded-sm border border-line bg-accent px-3 py-2 text-sm font-semibold text-bg disabled:cursor-not-allowed disabled:opacity-50"
              disabled={draft.trim() === "" || status !== "ready"}
              type="submit"
            >
              Send
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}

function renderMessage(message: AssistantMessage, fallbackToolResults: FallbackToolResult[]) {
  const visibleParts = message.parts.filter(shouldRenderPart);
  if (visibleParts.length === 0 && fallbackToolResults.length === 0) return null;

  return (
    <article
      key={message.id}
      className={`space-y-2 rounded-sm border px-3 py-2 ${
        message.role === "user" ? "border-accent/40 bg-accent/8" : "border-line bg-bg-3"
      }`}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[1.2px] text-dim">{message.role}</div>
      <div className="space-y-2 text-sm text-text">
        {visibleParts.map((part, index) => renderPart(part, index))}
        {fallbackToolResults.map((result) => renderFallbackToolResult(result))}
      </div>
    </article>
  );
}

function shouldRenderPart(part: AssistantPart): boolean {
  if (isTextUIPart(part)) return sanitizeDisplayText(part.text).trim() !== "";
  if (isToolUIPart(part)) return true;
  return false;
}

function renderPart(part: AssistantPart, index: number) {
  if (isTextUIPart(part)) {
    const text = sanitizeDisplayText(part.text);
    if (text.trim() === "") return null;
    return <p key={index} className="whitespace-pre-wrap leading-6">{text}</p>;
  }

  if (!isToolUIPart(part)) return null;

  return renderToolResultCard({
    key: `part-${String(index)}`,
    output: getToolOutput(part),
    state: getToolPartState(part),
    title: prettifyToolName(part.type.startsWith("tool-") ? part.type.slice(5) : part.type),
  });
}

function renderFallbackToolResult(result: FallbackToolResult) {
  return renderToolResultCard({
    key: result.key,
    output: result.output,
    state: "complete",
    title: `${prettifyToolName(result.toolName)} fallback`,
  });
}

function renderToolResultCard(options: {
  key: string;
  output: unknown;
  state: string;
  title: string;
}) {
  const message = readToolMessage(options.output);
  const palette = readPalette(options.output);
  const issues = readIssues(options.output);

  return (
    <div key={options.key} className="rounded-sm border border-line bg-bg-2 px-3 py-2">
      <div className="mb-1 flex items-center justify-between gap-3 text-xs">
        <span className="font-semibold text-text">{options.title}</span>
        <span className="uppercase tracking-[1.2px] text-dim">{options.state}</span>
      </div>
      {message && <p className="text-sm text-text whitespace-pre-wrap">{message}</p>}
      {palette && palette.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {palette.map((color) => (
            <div
              key={color}
              className="flex items-center gap-2 rounded-sm border border-line bg-bg-3 px-2 py-1 text-xs text-text"
            >
              <span className="h-4 w-4 rounded-[3px] border border-black/30" style={{ backgroundColor: color }} />
              {color}
            </div>
          ))}
        </div>
      )}
      {issues && issues.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs text-dim">
          {issues.map((issue) => <li key={issue}>{issue}</li>)}
        </ul>
      )}
      {!message && (!palette || palette.length === 0) && <p className="text-xs text-dim">Working…</p>}
    </div>
  );
}

async function executeToolCall(toolCall: ToolCallLike, context: ToolExecutionContext = {}): Promise<unknown> {
  switch (toolCall.toolName) {
    case "generate_palette":
      return generatePaletteFallback(toolCall.input, context);
    case "get_editor_state":
      return okOutput("Read current editor state.");
    case "set_frame_count": {
      const parsed = SetFrameCountInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(toolCall.toolName, "{ totalFrames }", parsed.error, toolCall.input, context);
      }
      setFrameCount(parsed.data.totalFrames);
      return okOutput(`Set frame count to ${String(parsed.data.totalFrames)}.`);
    }
    case "set_active_frame": {
      const parsed = SetActiveFrameInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(toolCall.toolName, "{ frameNumber }", parsed.error, toolCall.input, context);
      }
      selectFrame(parsed.data.frameNumber);
      return okOutput(`Selected frame ${String(parsed.data.frameNumber)}.`);
    }
    case "add_layer": {
      const parsed = AddLayerInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(toolCall.toolName, "{ count? }", parsed.error, toolCall.input, context);
      }
      const created = addLayer(parsed.data.count ?? 1);
      return okOutput(
        `Added ${String(created.length)} layer${created.length === 1 ? "" : "s"}: ${created.join(", ")}.`,
      );
    }
    case "select_layer": {
      const parsed = SelectLayerInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(toolCall.toolName, "{ layerId }", parsed.error, toolCall.input, context);
      }
      return selectLayer(parsed.data.layerId)
        ? okOutput(`Selected layer ${parsed.data.layerId}.`)
        : errorOutput(`Layer ${parsed.data.layerId} does not exist on the active frame.`);
    }
    case "set_layer_visibility": {
      const parsed = SetLayerVisibilityInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(toolCall.toolName, "{ layerId, visible }", parsed.error, toolCall.input, context);
      }
      return setLayerVisibility(parsed.data.layerId, parsed.data.visible)
        ? okOutput(`${parsed.data.visible ? "Showed" : "Hid"} layer ${parsed.data.layerId}.`)
        : errorOutput(`Layer ${parsed.data.layerId} does not exist on the active frame.`);
    }
    case "delete_layer": {
      const parsed = DeleteLayerInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(toolCall.toolName, "{ layerId }", parsed.error, toolCall.input, context);
      }
      return deleteLayer(parsed.data.layerId)
        ? okOutput(`Deleted layer ${parsed.data.layerId}.`)
        : errorOutput(`Could not delete layer ${parsed.data.layerId}. At least one layer must remain.`);
    }
    case "select_tool": {
      const parsed = SelectToolInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(toolCall.toolName, "{ tool }", parsed.error, toolCall.input, context);
      }
      selectTool(parsed.data.tool);
      return okOutput(`Selected ${parsed.data.tool} tool.`);
    }
    case "clear_active_layer": {
      const parsed = ClearActiveLayerInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(toolCall.toolName, "{ reason? }", parsed.error, toolCall.input, context);
      }
      const cleared = clearActiveLayer();
      return okOutput(
        cleared > 0
          ? `Cleared ${String(cleared)} painted pixel${cleared === 1 ? "" : "s"} from the active layer.`
          : "Active layer was already empty.",
      );
    }
    case "commit_frame": {
      const parsed = CommitFrameInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(
          toolCall.toolName,
          "{ frameIndex, layerId, rle, paletteOverride? }",
          parsed.error,
          toolCall.input,
          context,
        );
      }
      return commitFrame(parsed.data);
    }
    case "commit_tilemap": {
      const parsed = CommitTilemapInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(
          toolCall.toolName,
          "{ frameIndex, layerId, tileSize: 8, tiles, tilemap, paletteOverride? }",
          parsed.error,
          toolCall.input,
          context,
        );
      }
      return commitTilemap(parsed.data);
    }
    case "scaffold_animation_set": {
      const parsed = ScaffoldAnimationSetInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(
          toolCall.toolName,
          "{ character, fps?, initial, states: [{ id, frameCount, loop? }], transitions?: [{ from, to, event }] }",
          parsed.error,
          toolCall.input,
          context,
        );
      }
      return scaffoldAnimationSet(parsed.data);
    }
    // `propose_state_machine` is SERVER-EXECUTED in design-chat.ts. The
    // `onToolCall` channel does not fire for it — the proposal lands as a
    // tool-result UI part on the next assistant message. A useEffect below
    // picks up those parts and dispatches `proposal.set`.
    case "paint_line": {
      const parsed = PaintLineInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(
          toolCall.toolName,
          "{ from: { row, col }, to: { row, col }, color?, brushSize? }",
          parsed.error,
          toolCall.input,
          context,
        );
      }
      const painted = paintLine({
        brushSize: parsed.data.brushSize ?? getCurrentBrushSize(),
        color: parsed.data.color ?? getCurrentColor(),
        from: parsed.data.from,
        to: parsed.data.to,
      });
      return okOutput(
        painted > 0
          ? `Painted a line touching ${String(painted)} pixel${painted === 1 ? "" : "s"}.`
          : "Line paint made no visible change.",
      );
    }
    case "paint_rectangle": {
      const parsed = PaintShapeInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(
          toolCall.toolName,
          "{ from: { row, col }, to: { row, col }, color?, filled?, brushSize? }",
          parsed.error,
          toolCall.input,
          context,
        );
      }
      const painted = paintRectangle({
        brushSize: parsed.data.brushSize ?? getCurrentBrushSize(),
        color: parsed.data.color ?? getCurrentColor(),
        filled: parsed.data.filled ?? true,
        from: parsed.data.from,
        to: parsed.data.to,
      });
      return okOutput(
        painted > 0
          ? `Painted a ${parsed.data.filled === false ? "rectangle outline" : "filled rectangle"} touching ${
            String(painted)
          } pixel${painted === 1 ? "" : "s"}.`
          : "Rectangle paint made no visible change.",
      );
    }
    case "paint_ellipse": {
      const parsed = PaintShapeInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(
          toolCall.toolName,
          "{ from: { row, col }, to: { row, col }, color?, filled?, brushSize? }",
          parsed.error,
          toolCall.input,
          context,
        );
      }
      const painted = paintEllipse({
        brushSize: parsed.data.brushSize ?? getCurrentBrushSize(),
        color: parsed.data.color ?? getCurrentColor(),
        filled: parsed.data.filled ?? true,
        from: parsed.data.from,
        to: parsed.data.to,
      });
      return okOutput(
        painted > 0
          ? `Painted a ${parsed.data.filled === false ? "ellipse outline" : "filled ellipse"} touching ${
            String(painted)
          } pixel${painted === 1 ? "" : "s"}.`
          : "Ellipse paint made no visible change.",
      );
    }
    case "flood_fill_region": {
      const parsed = FloodFillInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(
          toolCall.toolName,
          "{ cell: { row, col }, color? }",
          parsed.error,
          toolCall.input,
          context,
        );
      }
      const painted = floodFillActiveLayer(parsed.data.cell, parsed.data.color ?? getCurrentColor());
      return okOutput(
        painted > 0
          ? `Flood-filled ${String(painted)} pixel${painted === 1 ? "" : "s"}.`
          : "Flood fill made no visible change.",
      );
    }
    case "set_color": {
      const parsed = SetColorInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(toolCall.toolName, "{ color }", parsed.error, toolCall.input, context);
      }
      store.trigger["color.set"]({ color: parsed.data.color });
      return okOutput(`Set color to ${parsed.data.color}.`);
    }
    case "set_fps": {
      const parsed = SetFpsInput.safeParse(toolCall.input);
      if (!parsed.success) return invalidToolInput(toolCall.toolName, "{ fps }", parsed.error, toolCall.input, context);
      store.trigger["fps.set"]({ fps: parsed.data.fps });
      return okOutput(`Set FPS to ${String(parsed.data.fps)}.`);
    }
    case "set_brush_size": {
      const parsed = SetBrushSizeInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(toolCall.toolName, "{ size }", parsed.error, toolCall.input, context);
      }
      store.trigger["brushSize.set"]({ size: parsed.data.size });
      return okOutput(`Set brush size to ${String(parsed.data.size)}.`);
    }
    case "set_mode": {
      const parsed = SetModeInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(toolCall.toolName, "{ mode }", parsed.error, toolCall.input, context);
      }
      store.trigger["mode.set"]({ mode: parsed.data.mode });
      return okOutput("Updated active tool mode.");
    }
    case "set_onion_enabled": {
      const parsed = SetOnionEnabledInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(toolCall.toolName, "{ enabled }", parsed.error, toolCall.input, context);
      }
      setOnionEnabled(parsed.data.enabled);
      return okOutput(`Turned onion skin ${parsed.data.enabled ? "on" : "off"}.`);
    }
    case "set_onion_opacity": {
      const parsed = SetOnionOpacityInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(toolCall.toolName, "{ value }", parsed.error, toolCall.input, context);
      }
      store.trigger["onionOpacity.set"]({ value: parsed.data.value });
      return okOutput("Updated onion opacity.");
    }
    case "set_project_name": {
      const parsed = SetProjectNameInput.safeParse(toolCall.input);
      if (!parsed.success) {
        return invalidToolInput(toolCall.toolName, "{ name }", parsed.error, toolCall.input, context);
      }
      store.trigger["project.rename"]({ name: parsed.data.name });
      return okOutput(`Renamed project to ${parsed.data.name}.`);
    }
    case "set_tab": {
      const parsed = SetTabInput.safeParse(toolCall.input);
      if (!parsed.success) return invalidToolInput(toolCall.toolName, "{ tab }", parsed.error, toolCall.input, context);
      store.trigger["tab.set"]({ tab: parsed.data.tab });
      return okOutput(`Switched to ${parsed.data.tab} tab.`);
    }
    case "validate_state_machine": {
      const report = validateCurrentStateMachine();
      if (!report) return errorOutput("No state machine exists yet.");
      return {
        ...okOutput(
          report.issues.length === 0
            ? "State machine has no issues."
            : `Found ${String(report.issues.length)} state-machine issue${report.issues.length === 1 ? "" : "s"}.`,
        ),
        issues: report.issues.map((issue) => `${issue.severity.toUpperCase()}: ${issue.message}`),
      };
    }
    default:
      return null;
  }
}

function commitFrame(input: z.infer<typeof CommitFrameInput>): unknown {
  const ctx = store.getSnapshot().context;
  if (input.frameIndex >= ctx.frames.length) {
    return errorOutput(
      `Frame index ${String(input.frameIndex)} out of range (have ${String(ctx.frames.length)} frames).`,
    );
  }
  const layers = ctx.frames[input.frameIndex] ?? [];
  const rows = layers[0]?.data.length ?? DEFAULT_ROWS;
  const cols = layers[0]?.data[0]?.length ?? DEFAULT_COLS;

  let data: ReturnType<typeof indicesToGrid>;
  try {
    const indices = expandRle(input.rle, rows * cols);
    data = indicesToGrid(indices, rows, cols, resolvePalette(input.paletteOverride));
  } catch (err) {
    return errorOutput(`commit_frame failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  const nextLayers = replaceLayerData(layers, input.layerId, data);
  if (!nextLayers) {
    return errorOutput(`Layer ${input.layerId} not found on frame ${String(input.frameIndex)}.`);
  }

  store.trigger["frame.replaceMany"]({
    entries: [{ frameIndex: input.frameIndex, layers: nextLayers }],
  });
  return okOutput(
    `Committed frame ${String(input.frameIndex)} layer ${input.layerId} (${String(rows * cols)} cells via RLE).`,
  );
}

function commitTilemap(input: z.infer<typeof CommitTilemapInput>): unknown {
  const ctx = store.getSnapshot().context;
  if (input.frameIndex >= ctx.frames.length) {
    return errorOutput(
      `Frame index ${String(input.frameIndex)} out of range (have ${String(ctx.frames.length)} frames).`,
    );
  }
  const layers = ctx.frames[input.frameIndex] ?? [];
  const rows = layers[0]?.data.length ?? DEFAULT_ROWS;
  const cols = layers[0]?.data[0]?.length ?? DEFAULT_COLS;

  let data: ReturnType<typeof tilemapToGrid>;
  try {
    data = tilemapToGrid(
      input.tiles,
      input.tilemap,
      input.tileSize,
      rows,
      cols,
      resolvePalette(input.paletteOverride),
    );
  } catch (err) {
    return errorOutput(`commit_tilemap failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  const nextLayers = replaceLayerData(layers, input.layerId, data);
  if (!nextLayers) {
    return errorOutput(`Layer ${input.layerId} not found on frame ${String(input.frameIndex)}.`);
  }

  store.trigger["frame.replaceMany"]({
    entries: [{ frameIndex: input.frameIndex, layers: nextLayers }],
  });
  return okOutput(
    `Committed frame ${String(input.frameIndex)} layer ${input.layerId} via ${
      String(input.tiles.length)
    } unique 8x8 tiles.`,
  );
}

function scaffoldAnimationSet(input: z.infer<typeof ScaffoldAnimationSetInput>): unknown {
  // Mirror the store's range computation so the tool output reflects exactly
  // what the reducer wrote. Keep the two in lock-step.
  let cursor = 0;
  const ranges: Record<string, [number, number]> = {};
  for (const state of input.states) {
    const start = cursor;
    const end = cursor + state.frameCount - 1;
    ranges[state.id] = [start, end];
    cursor = end + 1;
  }
  const totalFrames = cursor;

  store.trigger["machine.scaffold"]({ input });

  const transitionCount = input.transitions?.length ?? 0;
  return {
    ...okOutput(
      `Scaffolded ${input.character}: ${String(input.states.length)} state${input.states.length === 1 ? "" : "s"}, ${
        String(transitionCount)
      } transition${transitionCount === 1 ? "" : "s"}, ${String(totalFrames)} frame${
        totalFrames === 1 ? "" : "s"
      }. Initial state: ${input.initial}.`,
    ),
    ranges,
    totalFrames,
  };
}

async function generatePaletteFallback(input: unknown, context: ToolExecutionContext = {}): Promise<unknown> {
  const parsed = GeneratePaletteInput.safeParse(input);
  if (!parsed.success) {
    return invalidToolInput("generate_palette", "{ theme? | name?, size? }", parsed.error, input, context);
  }

  const theme = parsed.data.theme ?? parsed.data.name;
  if (!theme) {
    return invalidToolInputFromIssues(
      "generate_palette",
      "{ theme? | name?, size? }",
      ["theme: required when name is absent"],
      input,
      context,
    );
  }

  try {
    const response = await fetch("/api/ai/palette", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ theme, size: parsed.data.size }),
    });
    const body: unknown = await response.json().catch(() => null);
    const palette = readColorArray(body);
    const paletteTheme = readStringProp(body, "theme") ?? theme;
    if (!response.ok || !palette) {
      return errorOutput(`Palette generation failed for ${paletteTheme}.`);
    }
    return {
      ...okOutput(`Generated a ${String(palette.length)}-color palette for ${paletteTheme}.`),
      palette,
      theme: paletteTheme,
    };
  } catch {
    return errorOutput(`Palette generation failed for ${theme}.`);
  }
}

// Tool names the model is allowed to invoke via the "bare named" fallback
// pattern (e.g. `propose_state_machine: { ... }` without any <tool_call>
// wrapper). Limited to the modern batch tools; legacy `draw_pixel_art` and
// `setup_animation_scaffold` are intentionally absent.
const FALLBACK_TOOL_NAMES = [
  "propose_state_machine",
  "scaffold_animation_set",
  "commit_frame",
  "commit_tilemap",
  "update_working_notes",
] as const;

function extractFallbackToolCalls(message: AssistantMessage): ParsedFallbackToolCall[] {
  if (message.role !== "assistant") return [];

  const calls: ParsedFallbackToolCall[] = [];
  for (const [partIndex, part] of message.parts.entries()) {
    if (!isTextUIPart(part)) continue;
    const wrapped = extractToolCallBlocks(part.text);
    if (wrapped.length > 0) {
      pushParsedBlocks(calls, wrapped, message.id, partIndex);
      continue;
    }
    const standalone = extractStandaloneToolCallBlock(part.text);
    if (standalone.length > 0) {
      pushParsedBlocks(calls, standalone, message.id, partIndex);
      continue;
    }
    const inline = extractInlineNamedToolCallBlocks(part.text);
    if (inline.length === 0) continue;
    console.warn(
      `[ai-assistant] inline-named tool-call fallback fired for message ${message.id} (${
        inline.map((entry) => entry.toolName).join(", ")
      }); model regression — emitting bare JSON instead of a tool call.`,
    );
    for (const [toolIndex, entry] of inline.entries()) {
      calls.push({
        key: `${message.id}:${String(partIndex)}:inline-${String(toolIndex)}:${entry.toolName}`,
        messageId: message.id,
        toolCall: {
          input: entry.input,
          toolCallId: `${message.id}:${String(partIndex)}:inline-${String(toolIndex)}`,
          toolName: entry.toolName,
        },
      });
    }
  }
  return calls;
}

function pushParsedBlocks(
  calls: ParsedFallbackToolCall[],
  blocks: string[],
  messageId: string,
  partIndex: number,
): void {
  for (const [toolIndex, block] of blocks.entries()) {
    const parsed = parseRawToolCallBlock(block);
    if (!parsed) continue;
    calls.push({
      key: `${messageId}:${String(partIndex)}:${String(toolIndex)}:${parsed.toolName}`,
      messageId,
      toolCall: {
        input: parsed.input,
        toolCallId: `${messageId}:${String(partIndex)}:${String(toolIndex)}`,
        toolName: parsed.toolName,
      },
    });
  }
}

// Match `<tool_name>:` (or `<tool_name>` on its own line) followed by a JSON
// object literal. Used when the model emits a plan-in-prose like:
//
//     1. Propose state machine:
//     { "intent": "frog hop with swamp palette" }
//
// instead of invoking the tool. Each call site dispatches the JSON exactly
// like a real tool call would.
function extractInlineNamedToolCallBlocks(text: string): { input: unknown; toolName: string }[] {
  const results: { input: unknown; toolName: string }[] = [];
  for (const toolName of FALLBACK_TOOL_NAMES) {
    const pattern = new RegExp(`(?:^|[\\s\`>*_-])${toolName}\\b\\s*[:\\-]?\\s*\\n?\\s*(\\{)`, "g");
    let match = pattern.exec(text);
    while (match) {
      const braceIndex = match.index + match[0].length - 1;
      const block = sliceBalancedJsonObject(text, braceIndex);
      if (!block) continue;
      const parsed = parseJsonish(block);
      if (!parsed || typeof parsed !== "object") continue;
      results.push({ input: parsed, toolName });
      pattern.lastIndex = braceIndex + block.length;
      match = pattern.exec(text);
    }
  }
  return results;
}

function sliceBalancedJsonObject(text: string, start: number): string | null {
  if (text.charAt(start) !== "{") return null;
  let depth = 0;
  let inString = false;
  let escaping = false;
  for (let index = start; index < text.length; index += 1) {
    const char = text.charAt(index);
    if (escaping) {
      escaping = false;
      continue;
    }
    if (char === "\\" && inString) {
      escaping = true;
      continue;
    }
    if (char === "\"") {
      inString = !inString;
      continue;
    }
    if (inString) continue;
    if (char === "{") depth += 1;
    else if (char === "}") {
      depth -= 1;
      if (depth === 0) return text.slice(start, index + 1);
    }
  }
  return null;
}

function extractToolCallBlocks(text: string): string[] {
  const blocks: string[] = [];
  const pattern = /<tool_call>\s*([\s\S]*?)\s*<\/tool_call>/g;
  for (const match of text.matchAll(pattern)) {
    const block = match[1];
    if (block) blocks.push(block);
  }
  return blocks;
}

function extractStandaloneToolCallBlock(text: string): string[] {
  const trimmed = stripCodeFence(text.trim());
  if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) return [];
  return parseRawToolCallBlock(trimmed) ? [trimmed] : [];
}

function parseRawToolCallBlock(block: string): { input: unknown; toolName: string } | null {
  const parsed = parseJsonish(block);
  const fallbackName = readRawStringField(block, "name");
  const toolName = readStringProp(parsed, "name") ?? fallbackName;
  if (!toolName) return null;

  const rawArguments = readUnknownProp(parsed, "arguments") ?? readUnknownProp(parsed, "parameters")
    ?? readRawStringField(block, "arguments") ?? readRawStringField(block, "parameters");
  const input = typeof rawArguments === "string" ? parseJsonish(rawArguments) ?? rawArguments : rawArguments ?? {};
  return { input, toolName };
}

function parseJsonish(text: string): unknown {
  const trimmed = stripCodeFence(text.trim());

  try {
    return JSON.parse(trimmed);
  } catch {
    return parseNormalizedJsonish(trimmed);
  }
}

function parseNormalizedJsonish(text: string): unknown {
  const normalized = text
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/i, "")
    .replace(/\bTrue\b/g, "true")
    .replace(/\bFalse\b/g, "false")
    .replace(/\bNone\b/g, "null")
    .replace(/'/g, "\"");

  try {
    return JSON.parse(normalized);
  } catch {
    return null;
  }
}

function stripCodeFence(text: string): string {
  return text.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/i, "");
}

function readRawStringField(text: string, key: string): string | null {
  const singleQuoted = new RegExp(`["']${key}["']\\s*:\\s*'([\\s\\S]*?)'(?=\\s*[,}])`).exec(text);
  if (singleQuoted?.[1]) return singleQuoted[1];

  const doubleQuoted = new RegExp(`["']${key}["']\\s*:\\s*"([\\s\\S]*?)"(?=\\s*[,}])`).exec(text);
  return doubleQuoted?.[1] ?? null;
}

function sanitizeDisplayText(text: string): string {
  const hadToolBlocks = /<tool_call>[\s\S]*?<\/tool_call>/.test(text);
  const withoutToolBlocks = text
    .replace(/<tool_call>[\s\S]*?<\/tool_call>/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  if (extractStandaloneToolCallBlock(withoutToolBlocks).length > 0) return "";
  return hadToolBlocks ? trimTrailingToolLeadIn(withoutToolBlocks) : withoutToolBlocks;
}

function trimTrailingToolLeadIn(text: string): string {
  const lines = text.split("\n");
  for (let index = lines.length - 1; index >= 0; index -= 1) {
    const line = lines[index]?.trim() ?? "";
    if (line === "") continue;
    if (/[.!?:;,)\]}"'`]$/.test(line)) return text;
    lines.splice(index, 1);
    return lines.join("\n").trimEnd();
  }
  return text;
}

function okOutput(message: string) {
  return {
    editor: summarizeEditorContext(store.getSnapshot().context),
    message,
    ok: true,
  };
}

function errorOutput(message: string) {
  return {
    editor: summarizeEditorContext(store.getSnapshot().context),
    message,
    ok: false,
  };
}

function invalidToolInput(
  toolName: string,
  expectedShape: string,
  error: z.ZodError,
  received: unknown,
  context: ToolExecutionContext = {},
) {
  return invalidToolInputFromIssues(
    toolName,
    expectedShape,
    error.issues.map(formatZodIssue),
    received,
    context,
  );
}

function invalidToolInputFromIssues(
  toolName: string,
  expectedShape: string,
  issues: string[],
  received: unknown,
  context: ToolExecutionContext = {},
) {
  const attempt = nextInvalidInputAttempt(toolName, context);
  const shouldRetry = attempt < MAX_TOOL_INPUT_ATTEMPTS;
  return {
    code: "INVALID_TOOL_INPUT",
    editor: summarizeEditorContext(store.getSnapshot().context),
    expectedShape,
    issues,
    message: shouldRetry
      ? `Tool input invalid for ${toolName}. Retry once with corrected args.`
      : `Tool input invalid for ${toolName}. Retry limit reached; do not call this tool again for this request.`,
    ok: false,
    received,
    retry: {
      attempt,
      maxAttempts: MAX_TOOL_INPUT_ATTEMPTS,
      shouldRetry,
    },
    toolName,
  };
}

function nextInvalidInputAttempt(toolName: string, context: ToolExecutionContext): number {
  const attempts = context.invalidInputAttempts;
  if (!attempts) return 1;
  const next = Math.min(MAX_TOOL_INPUT_ATTEMPTS, (attempts.get(toolName) ?? 0) + 1);
  attempts.set(toolName, next);
  return next;
}

function formatZodIssue(issue: z.core.$ZodIssue): string {
  const path = issue.path.length > 0 ? issue.path.join(".") : "input";
  return `${path}: ${issue.message}`;
}

function prettifyToolName(name: string): string {
  return name.split("_").map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1)).join(" ");
}

function readToolMessage(output: unknown): string | null {
  const message = readStringProp(output, "message");
  if (message) return message;

  const structuredContent = readUnknownProp(output, "structuredContent");
  const structuredPalette = readColorArray(structuredContent);
  const structuredTheme = readStringProp(structuredContent, "theme");
  if (structuredPalette && structuredTheme) {
    return `Generated a ${String(structuredPalette.length)}-color palette for ${structuredTheme}.`;
  }

  const palette = readColorArray(output);
  const theme = readStringProp(output, "theme");
  if (palette && theme) {
    return `Generated a ${String(palette.length)}-color palette for ${theme}.`;
  }

  return null;
}

function readPalette(output: unknown): string[] | null {
  const palette = readColorArray(output);
  if (palette) return palette;
  return readColorArray(readUnknownProp(output, "structuredContent"));
}

function readIssues(output: unknown): string[] | null {
  const rawIssues = readUnknownProp(output, "issues");
  if (!Array.isArray(rawIssues)) return null;
  const issues = rawIssues.filter((issue): issue is string => typeof issue === "string");
  return issues.length > 0 ? issues : null;
}

function readColorArray(value: unknown): string[] | null {
  const rawPalette = readUnknownProp(value, "palette");
  if (!Array.isArray(rawPalette)) return null;
  const palette = rawPalette.filter((entry): entry is string => typeof entry === "string");
  return palette.length > 0 ? palette : null;
}

function readStringProp(value: unknown, key: string): string | null {
  const prop = readUnknownProp(value, key);
  return typeof prop === "string" ? prop : null;
}

function readUnknownProp(value: unknown, key: string): unknown {
  if (!value || typeof value !== "object") return undefined;
  return Reflect.get(value, key);
}
